import { masterMark } from '@deltasuite/ui-tokens';
import { useEffect, useState } from 'react';
import { getPreferences } from '../../api/preferences';
import { getToday } from '../../api/today';
import type { SprintConfig } from './types';

interface Props {
  contactCount: number;
  onStart: (config: SprintConfig) => void;
  onCancel: () => void;
}

// R4: daily points target replaces sprint duration as the unit of work.
// One field, one number. Default seeded from user_preferences.daily_points_target
// so reps don't re-enter it every sprint. Server-side 15-minute lease expiry
// stays untouched.
export function SprintSetup(props: Props) {
  const [target, setTarget] = useState(30);
  const [startingPoints, setStartingPoints] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void Promise.all([getPreferences(), getToday()])
      .then(([prefs, today]) => {
        if (cancelled) return;
        // Prefer today's actual target (which already factors in any prefs
        // change since module load); fall back to prefs default.
        setTarget(today.target ?? prefs.daily_points_target);
        setStartingPoints(today.points);
      })
      .catch(() => {
        // Stick with the 30-default and 0 starting points — failure here is
        // fine, the UI lets the rep override the target anyway.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <main className="flex min-h-full flex-col items-center justify-center px-8 py-16">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 text-graphite-700">
          <span className="font-sans text-2xl leading-none text-signal-amber" aria-hidden>
            {masterMark}
          </span>
          <span className="font-mono text-xs uppercase tracking-[0.18em] text-graphite-500">
            DeltaDial
          </span>
        </div>

        <h1 className="mt-10 font-sans text-3xl font-semibold tracking-tight text-graphite-900">
          Lock the list?
        </h1>
        <p className="mt-2 font-sans text-sm leading-relaxed text-graphite-700">
          {props.contactCount} contacts queued. Sprint runs until you hit your target or work
          through the list.
        </p>

        <div className="mt-8">
          <label>
            <span className="font-mono text-[0.7rem] uppercase tracking-[0.18em] text-graphite-500">
              Today’s target
            </span>
            <div className="mt-1 flex items-baseline gap-2 rounded-md border border-graphite-300 bg-surface px-3 py-2">
              <input
                type="number"
                min={1}
                max={500}
                value={target}
                onChange={(e) => setTarget(parseInt(e.target.value, 10) || 30)}
                className="w-full bg-transparent font-sans text-2xl font-semibold text-graphite-900 outline-none"
              />
              <span className="font-mono text-xs text-graphite-500">pts</span>
            </div>
          </label>
        </div>

        <div className="mt-10 flex justify-end gap-2">
          <button
            type="button"
            onClick={props.onCancel}
            className="rounded-md px-4 py-2 font-sans text-sm text-graphite-700 hover:bg-surface-sunk"
          >
            Back
          </button>
          <button
            type="button"
            disabled={props.contactCount === 0}
            onClick={() => props.onStart({ pointTarget: target, startingPoints })}
            className="rounded-md bg-signal-amber px-4 py-2 font-sans text-sm font-medium text-surface hover:bg-signal-amber-hover disabled:opacity-40"
          >
            Start sprint
          </button>
        </div>
      </div>
    </main>
  );
}
